import type {
  AcquisitionAssignmentRevision,
  CreativeDecisionPortfolioResult,
  CreativeDecisionProjection,
  MoneyAmountBinding,
} from './contracts';
import { hasCanonicalDeploymentKey } from './identity';

export interface CreativeDecisionValidationIssue {
  code: string;
  path: string;
  message: string;
}

export class CreativeDecisionContractError extends Error {
  constructor(readonly issues: CreativeDecisionValidationIssue[]) {
    super(`Creative decision response failed validation: ${issues.map((entry) => `${entry.code} at ${entry.path}`).join('; ')}`);
    this.name = 'CreativeDecisionContractError';
  }
}

const PROJECTION_ROOT_KEYS = new Set([
  'projectionVersion', 'projectionId', 'copilotEvidenceRef', 'scopeRef', 'deployment', 'capability',
  'artifactSnapshots', 'artifactBindings', 'display', 'lifecycleStage',
  'lifecycleEvents', 'dataState', 'dataStateReasonCodes', 'recommendation',
  'decisionHistory', 'decisionSupport', 'evidence', 'stageMetrics', 'monetizationContexts',
  'monetization', 'money', 'calendarDynamics', 'productEconomics', 'fatigueCurve', 'watchOnlyPltv', 'sourceLinks', 'filterContext',
  'artifactContext', 'freshness', 'maturity', 'detailSource',
]);
const FILTER_CONTEXT_KEYS = new Set(['funnels', 'geos', 'planIds']);
const PORTFOLIO_ROOT_KEYS = new Set([
  'projectionVersion', 'scopeRef', 'rows', 'summary', 'page', 'sourceCoverage',
  'filterFacets',
]);
const PRIVATE_KEY = /(?:^|_)(?:access|refresh|id)?token(?:$|_)|password|secret|credential|private(?:artifact|path|payload)|storage(?:path|locator)|raw(?:answer|email|payload)/i;
const PRIVATE_VALUE = /(?:bearer\s+[\w.-]+|[\w.+-]+@[\w.-]+\.[A-Za-z]{2,}|gs:\/\/|\/Users\/|\/private\/|-----BEGIN)/i;

export function validateCreativeDecisionProjection(
  projection: CreativeDecisionProjection,
): CreativeDecisionValidationIssue[] {
  const issues: CreativeDecisionValidationIssue[] = [];
  if (!hasCanonicalDeploymentKey(projection.deployment)) {
    issues.push(issue('deployment_key_mismatch', 'deployment.deploymentKey', 'Deployment key does not match canonical provider identity.'));
  }
  const evidenceByKey = new Map<string, number>();
  for (const [index, binding] of projection.evidence.entries()) {
    evidenceByKey.set(binding.metricKey, (evidenceByKey.get(binding.metricKey) ?? 0) + 1);
    if (!binding.scopeRef) issues.push(issue('evidence_scope_missing', `evidence[${index}].scopeRef`, 'Every metric needs an immutable scope reference.'));
    if (binding.coverage && binding.coverage.denominator <= 0) {
      issues.push(issue('coverage_denominator_invalid', `evidence[${index}].coverage.denominator`, 'Coverage denominator must be positive.'));
    }
    if (binding.coverage && binding.coverage.numerator > binding.coverage.denominator) {
      issues.push(issue('coverage_exceeds_denominator', `evidence[${index}].coverage`, 'Coverage numerator cannot exceed its denominator.'));
    }
  }
  for (const [key, count] of evidenceByKey.entries()) {
    if (count !== 1) issues.push(issue('evidence_binding_not_unique', 'evidence', `Metric ${key} has ${count} evidence bindings; exactly one is required.`));
  }
  for (const key of projection.recommendation.decisionMetricKeys) {
    if (evidenceByKey.get(key) !== 1) {
      issues.push(issue('decision_evidence_missing', 'recommendation.decisionMetricKeys', `Decision metric ${key} does not have exactly one evidence binding.`));
    }
    // Structural watch-only ban (per-creative brief, decision 2.1): the
    // watch-only pLTV lane may NEVER drive a decision — not via its canonical
    // key nor any watch_only-prefixed variant a future change could add.
    // C2-AC-007 extends the ban to the cadence-signal family: the weekly
    // forecast (sbg_weekly), signalState, and cadence-signal evidence are
    // watch-only until a C4-registered calibration — a hit here is a release
    // blocker, matching the shipped fatigue precedent.
    if (/(^|_)watch_only/i.test(key) || /(^|_)(sbg_weekly|cadence_signal|signal_state|weekly_pltv)/i.test(key)) {
      issues.push(issue(
        'watch_only_metric_in_decision',
        'recommendation.decisionMetricKeys',
        `Watch-only metric ${key} can never enter decisionMetricKeys.`,
      ));
    }
  }
  validateRecommendationActionTarget(projection, issues);
  for (const [index, metric] of (projection.stageMetrics ?? []).entries()) {
    const path = `stageMetrics[${index}]`;
    if (metric.denominator !== undefined && (!Number.isFinite(metric.denominator) || metric.denominator <= 0)) {
      issues.push(issue('stage_metric_denominator_invalid', `${path}.denominator`, 'A stage-metric denominator must be finite and positive.'));
    }
    if (
      metric.unit === 'ratio'
      && metric.numerator !== undefined
      && metric.denominator !== undefined
      && metric.denominator > 0
      && metric.value !== undefined
      && Math.abs(metric.value - metric.numerator / metric.denominator) > 1e-9
    ) {
      issues.push(issue('stage_metric_ratio_inconsistent', `${path}.value`, 'A ratio must reconcile to its named numerator and denominator.'));
    }
    const drivesDecision = projection.recommendation.decisionMetricKeys.includes(metric.metricKey);
    if (drivesDecision && metric.evidence.role !== 'validated_treatment_surrogate') {
      issues.push(issue(
        'early_signal_decision_role_invalid',
        `${path}.evidence.role`,
        'Diagnostic and predictive-feature stage metrics cannot directly drive a decision.',
      ));
    }
    const decisionEvidence = drivesDecision
      ? projection.evidence.find((binding) => binding.metricKey === metric.metricKey)
      : undefined;
    if (
      drivesDecision
      && metric.evidence.role === 'validated_treatment_surrogate'
      && (
        !decisionEvidence
        || !['randomized', 'incremental'].includes(decisionEvidence.evidenceClass)
        || !decisionEvidence.counterfactualAvailable
      )
    ) {
      issues.push(issue(
        'surrogate_counterfactual_evidence_missing',
        `${path}.evidence`,
        'A treatment-surrogate decision needs randomized or incremental counterfactual evidence.',
      ));
    }
    if (metric.evidence.role === 'validated_treatment_surrogate') {
      if (!metric.evidence.randomizedUnitRef) {
        issues.push(issue('surrogate_randomized_unit_missing', `${path}.evidence.randomizedUnitRef`, 'A validated treatment surrogate needs its randomized unit.'));
      }
      if (!metric.evidence.modelOrValidationVersion) {
        issues.push(issue('surrogate_validation_version_missing', `${path}.evidence.modelOrValidationVersion`, 'A validated treatment surrogate needs an immutable validation version.'));
      }
      if (
        metric.evidence.expiresAt
        && Number.isFinite(Date.parse(metric.evidence.expiresAt))
        && Date.parse(metric.evidence.expiresAt) <= Date.parse(projection.freshness.asOf)
        && drivesDecision
      ) {
        issues.push(issue('surrogate_expired', `${path}.evidence.expiresAt`, 'An expired surrogate cannot drive a current decision.'));
      }
    }
  }
  validateDecisionHistory(projection, issues);
  if (projection.dataState === 'too_young') {
    const nextReadAt = projection.recommendation.nextReadAt ?? projection.maturity?.nextReadAt;
    // An explicitly unknown cohort age cannot register a read timestamp —
    // demanding one would force the server to fabricate it. Every other
    // too-young row (computable age, or no maturity object at all) must
    // still expose its registered next read.
    const ageUnknown = projection.maturity?.state === 'unknown';
    if (!ageUnknown && (!nextReadAt || !Number.isFinite(Date.parse(nextReadAt)))) {
      issues.push(issue(
        'next_read_at_missing',
        'recommendation.nextReadAt',
        'A too-young decision must expose its next registered read timestamp.',
      ));
    }
  }
  if (projection.maturity?.state === 'immature' && projection.dataState !== 'too_young') {
    issues.push(issue(
      'maturity_data_state_mismatch',
      'maturity.state',
      'An explicitly immature cohort must use the too_young data state.',
    ));
  }
  if (
    projection.capability.metricAdditivity === 'non_summable_directional'
    && projection.evidence.some((binding) => /(^|_)(pltv|roas|contribution)($|_)/i.test(binding.metricKey))
    && projection.capability.artifactContribution !== 'available'
  ) {
    issues.push(issue('non_summable_asset_value_claim', 'evidence', 'Directional child assets cannot carry inherited pLTV, ROAS, or contribution evidence.'));
  }
  return issues;
}

function validateRecommendationActionTarget(
  projection: CreativeDecisionProjection,
  issues: CreativeDecisionValidationIssue[],
): void {
  const action = projection.recommendation.primaryAction;
  const target = projection.recommendation.actionTarget;
  if (!target) {
    issues.push(issue(
      'action_target_missing',
      'recommendation.actionTarget',
      'Every primary action needs one server-projected destination.',
    ));
    return;
  }
  const valid = action === 'review_scale_probe'
    ? target.kind === 'experiment_draft' && target.mode === 'creative-decision-experiment-draft'
    : action === 'review_next_read'
      ? target.kind === 'experiment_draft' && target.mode === 'creative-decision-next-read'
      : action === 'create_refresh_experiment'
        ? target.kind === 'refresh_draft' && target.mode === 'refresh-experiment-draft'
        : action === 'review_stop_evidence'
          ? target.kind === 'stop_evidence_review'
          : action === 'open_evidence' || action === 'open_measurement_repair'
            ? target.kind === 'source_link'
            : action === 'none' && target.kind === 'none';
  if (!valid) {
    issues.push(issue(
      'action_target_mismatch',
      'recommendation.actionTarget',
      'The server-projected destination does not match the primary action.',
    ));
    return;
  }
  if (target.kind === 'source_link') {
    const matches = (projection.sourceLinks ?? [])
      .filter((source) => source.kind === target.sourceLinkKind);
    if (matches.length !== 1) {
      issues.push(issue(
        'action_source_target_ambiguous',
        'recommendation.actionTarget.sourceLinkKind',
        'A source-link action must resolve to exactly one response link.',
      ));
    }
  }
}

export function validateCreativeDecisionPortfolioResult(
  portfolio: CreativeDecisionPortfolioResult,
): CreativeDecisionValidationIssue[] {
  const issues: CreativeDecisionValidationIssue[] = [];
  for (const [index, row] of portfolio.rows.entries()) {
    if (row.projectionVersion !== portfolio.projectionVersion) {
      issues.push(issue(
        'portfolio_projection_version_mismatch',
        `rows[${index}].projectionVersion`,
        'Every row must use the immutable portfolio projection version.',
      ));
    }
    if (row.scopeRef !== portfolio.scopeRef) {
      issues.push(issue(
        'portfolio_scope_mismatch',
        `rows[${index}].scopeRef`,
        'Every row must use the immutable portfolio scope.',
      ));
    }
    issues.push(...validateCreativeDecisionProjection(row).map((entry) => ({
      ...entry,
      path: `rows[${index}].${entry.path}`,
    })));
  }
  const providerCoverage = portfolio.sourceCoverage.providerCoverage ?? [];
  const providers = new Set<string>();
  let coveredRows = 0;
  for (const [index, coverage] of providerCoverage.entries()) {
    const path = `sourceCoverage.providerCoverage[${index}]`;
    if (providers.has(coverage.provider)) {
      issues.push(issue('provider_coverage_duplicate', `${path}.provider`, 'A provider can appear only once in source coverage.'));
    }
    providers.add(coverage.provider);
    coveredRows += coverage.rowCount;
    if (coverage.state === 'available' && coverage.rowCount === 0) {
      issues.push(issue('provider_coverage_false_available', `${path}.state`, 'Available provider coverage requires at least one projected row.'));
    }
    if (
      ['no_data', 'not_connected', 'connector_disabled', 'projection_unavailable'].includes(coverage.state)
      && coverage.rowCount !== 0
    ) {
      issues.push(issue('provider_coverage_state_mismatch', `${path}.rowCount`, `${coverage.state} provider coverage cannot claim projected rows.`));
    }
  }
  if (providerCoverage.length > 0 && coveredRows !== portfolio.sourceCoverage.providerRows) {
    issues.push(issue(
      'provider_coverage_rows_do_not_reconcile',
      'sourceCoverage.providerCoverage',
      'Provider row counts must reconcile to the portfolio provider-row control.',
    ));
  }
  return issues;
}

export function assertValidCreativeDecisionProjection(
  value: unknown,
): CreativeDecisionProjection {
  const shapeIssues = validateProjectionPayloadShape(value, 'projection');
  if (shapeIssues.length) throw new CreativeDecisionContractError(shapeIssues);
  const projection = value as CreativeDecisionProjection;
  const issues = validateCreativeDecisionProjection(projection);
  if (issues.length) throw new CreativeDecisionContractError(issues);
  return projection;
}

export function assertValidCreativeDecisionPortfolioResult(
  value: unknown,
): CreativeDecisionPortfolioResult {
  const shapeIssues = validatePortfolioPayloadShape(value);
  if (shapeIssues.length) throw new CreativeDecisionContractError(shapeIssues);
  const portfolio = value as CreativeDecisionPortfolioResult;
  const issues = validateCreativeDecisionPortfolioResult(portfolio);
  if (issues.length) throw new CreativeDecisionContractError(issues);
  return portfolio;
}

function validatePortfolioPayloadShape(value: unknown): CreativeDecisionValidationIssue[] {
  if (!isRecord(value)) {
    return [issue('portfolio_shape_invalid', 'portfolio', 'Creative decision portfolio must be an object.')];
  }
  const issues = unknownRootKeys(value, PORTFOLIO_ROOT_KEYS, 'portfolio');
  issues.push(...privatePayloadIssues(value, 'portfolio'));
  if (!nonEmptyString(value.projectionVersion)) issues.push(issue('portfolio_shape_invalid', 'portfolio.projectionVersion', 'Projection version is required.'));
  if (!nonEmptyString(value.scopeRef)) issues.push(issue('portfolio_shape_invalid', 'portfolio.scopeRef', 'Scope reference is required.'));
  if (!Array.isArray(value.rows)) {
    issues.push(issue('portfolio_shape_invalid', 'portfolio.rows', 'Rows must be an array.'));
  } else {
    value.rows.forEach((row, index) => issues.push(...validateProjectionPayloadShape(row, `portfolio.rows[${index}]`)));
  }
  for (const key of ['summary', 'page', 'sourceCoverage'] as const) {
    if (!isRecord(value[key])) issues.push(issue('portfolio_shape_invalid', `portfolio.${key}`, `${key} must be an object.`));
  }
  if (isRecord(value.sourceCoverage)) {
    if (
      value.sourceCoverage.deploymentScope !== undefined
      && !['funnel_linked', 'all'].includes(String(value.sourceCoverage.deploymentScope))
    ) {
      issues.push(issue('portfolio_shape_invalid', 'portfolio.sourceCoverage.deploymentScope', 'deploymentScope must be funnel_linked or all.'));
    }
    if (
      value.sourceCoverage.outOfScopeRows !== undefined
      && (!Number.isSafeInteger(value.sourceCoverage.outOfScopeRows) || Number(value.sourceCoverage.outOfScopeRows) < 0)
    ) {
      issues.push(issue('portfolio_shape_invalid', 'portfolio.sourceCoverage.outOfScopeRows', 'outOfScopeRows must be a non-negative safe integer.'));
    }
    // Cached-first serving marker (topup plan S1): only the two known values
    // may render, and a snapshot MUST carry its computation time — the banner
    // is the honesty carrier, not decoration.
    if (
      value.sourceCoverage.servedFrom !== undefined
      && !['snapshot', 'live'].includes(String(value.sourceCoverage.servedFrom))
    ) {
      issues.push(issue('portfolio_shape_invalid', 'portfolio.sourceCoverage.servedFrom', 'servedFrom must be snapshot or live.'));
    }
    for (const key of ['snapshotComputedAt', 'snapshotWindowFrom', 'snapshotWindowTo'] as const) {
      const marker = value.sourceCoverage[key];
      if (marker !== undefined && (typeof marker !== 'string' || !marker.trim())) {
        issues.push(issue('portfolio_shape_invalid', `portfolio.sourceCoverage.${key}`, `${key} must be a non-empty string when present.`));
      }
    }
    if (value.sourceCoverage.servedFrom === 'snapshot' && value.sourceCoverage.snapshotComputedAt === undefined) {
      issues.push(issue('portfolio_shape_invalid', 'portfolio.sourceCoverage.snapshotComputedAt', 'A snapshot-served portfolio must state when the snapshot was computed.'));
    }
    if (
      value.sourceCoverage.snapshotReadDegraded !== undefined
      && value.sourceCoverage.snapshotReadDegraded !== true
    ) {
      issues.push(issue('portfolio_shape_invalid', 'portfolio.sourceCoverage.snapshotReadDegraded', 'snapshotReadDegraded may only be true when present.'));
    }
    if (value.sourceCoverage.snapshotReadDegraded === true && value.sourceCoverage.servedFrom === 'snapshot') {
      issues.push(issue('portfolio_shape_invalid', 'portfolio.sourceCoverage.snapshotReadDegraded', 'A degraded snapshot read can only accompany a live-served portfolio.'));
    }
  }
  if (isRecord(value.sourceCoverage) && value.sourceCoverage.providerCoverage !== undefined) {
    if (!Array.isArray(value.sourceCoverage.providerCoverage)) {
      issues.push(issue('provider_coverage_shape_invalid', 'portfolio.sourceCoverage.providerCoverage', 'Provider coverage must be an array.'));
    } else {
      value.sourceCoverage.providerCoverage.forEach((entry, index) => {
        const path = `portfolio.sourceCoverage.providerCoverage[${index}]`;
        if (!isRecord(entry)) {
          issues.push(issue('provider_coverage_shape_invalid', path, 'Provider coverage entries must be objects.'));
          return;
        }
        if (!['meta', 'google_ads', 'tiktok_ads'].includes(String(entry.provider))) {
          issues.push(issue('provider_coverage_shape_invalid', `${path}.provider`, 'Provider coverage uses an unknown provider.'));
        }
        if (![
          'available', 'no_data', 'not_connected', 'needs_reconnect',
          'connector_disabled', 'projection_unavailable',
        ].includes(String(entry.state))) {
          issues.push(issue('provider_coverage_shape_invalid', `${path}.state`, 'Provider coverage uses an unknown state.'));
        }
        if (typeof entry.selectedByRequest !== 'boolean') {
          issues.push(issue('provider_coverage_shape_invalid', `${path}.selectedByRequest`, 'selectedByRequest must be boolean.'));
        }
        for (const countKey of ['accountCount', 'rowCount'] as const) {
          if (!Number.isSafeInteger(entry[countKey]) || Number(entry[countKey]) < 0) {
            issues.push(issue('provider_coverage_shape_invalid', `${path}.${countKey}`, `${countKey} must be a non-negative safe integer.`));
          }
        }
        if (!Array.isArray(entry.reasonCodes) || entry.reasonCodes.some((reason) => !nonEmptyString(reason))) {
          issues.push(issue('provider_coverage_shape_invalid', `${path}.reasonCodes`, 'reasonCodes must contain non-empty strings.'));
        }
        if (!nonEmptyString(entry.asOf) || !Number.isFinite(Date.parse(String(entry.asOf)))) {
          issues.push(issue('provider_coverage_shape_invalid', `${path}.asOf`, 'Provider coverage requires an ISO timestamp.'));
        }
      });
    }
  }
  return dedupeIssues(issues);
}

function validateProjectionPayloadShape(
  value: unknown,
  path: string,
): CreativeDecisionValidationIssue[] {
  if (!isRecord(value)) {
    return [issue('projection_shape_invalid', path, 'Creative decision projection must be an object.')];
  }
  const issues = unknownRootKeys(value, PROJECTION_ROOT_KEYS, path);
  issues.push(...privatePayloadIssues(value, path));
  for (const key of ['projectionVersion', 'projectionId', 'scopeRef', 'lifecycleStage', 'dataState'] as const) {
    if (!nonEmptyString(value[key])) issues.push(issue('projection_shape_invalid', `${path}.${key}`, `${key} is required.`));
  }
  for (const key of ['deployment', 'capability', 'recommendation', 'monetization', 'money', 'freshness'] as const) {
    if (!isRecord(value[key])) issues.push(issue('projection_shape_invalid', `${path}.${key}`, `${key} must be an object.`));
  }
  for (const key of ['artifactSnapshots', 'dataStateReasonCodes', 'evidence'] as const) {
    if (!Array.isArray(value[key])) issues.push(issue('projection_shape_invalid', `${path}.${key}`, `${key} must be an array.`));
  }
  if (isRecord(value.deployment)) {
    for (const key of ['projectId', 'provider', 'providerAccountId', 'decisionUnitKind', 'providerResourceName', 'deploymentKey', 'firstObservedAt']) {
      if (!nonEmptyString(value.deployment[key])) issues.push(issue('projection_shape_invalid', `${path}.deployment.${key}`, `${key} is required.`));
    }
    if (
      value.deployment.destinationClass !== undefined
      && !['web', 'store', 'unknown'].includes(String(value.deployment.destinationClass))
    ) {
      issues.push(issue('projection_shape_invalid', `${path}.deployment.destinationClass`, 'destinationClass must be web, store, or unknown.'));
    }
  }
  if (isRecord(value.freshness)) {
    if (!nonEmptyString(value.freshness.asOf)) issues.push(issue('projection_shape_invalid', `${path}.freshness.asOf`, 'Freshness timestamp is required.'));
    if (!Array.isArray(value.freshness.sourceRevisionRefs)) issues.push(issue('projection_shape_invalid', `${path}.freshness.sourceRevisionRefs`, 'Source revision references must be an array.'));
  }
  if (value.maturity !== undefined && !isRecord(value.maturity)) {
    issues.push(issue('projection_shape_invalid', `${path}.maturity`, 'maturity must be an object when present.'));
  }
  if (value.calendarDynamics !== undefined) {
    if (!isRecord(value.calendarDynamics)) {
      issues.push(issue('projection_shape_invalid', `${path}.calendarDynamics`, 'calendarDynamics must be an object when present.'));
    } else {
      if (!nonEmptyString(value.calendarDynamics.state)) {
        issues.push(issue('projection_shape_invalid', `${path}.calendarDynamics.state`, 'calendarDynamics.state is required.'));
      }
      for (const key of ['cells', 'products', 'reasonCodes', 'sourceRefs'] as const) {
        if (!Array.isArray(value.calendarDynamics[key])) {
          issues.push(issue('projection_shape_invalid', `${path}.calendarDynamics.${key}`, `calendarDynamics.${key} must be an array.`));
        }
      }
    }
  }
  if (value.productEconomics !== undefined) {
    if (!isRecord(value.productEconomics)) {
      issues.push(issue('projection_shape_invalid', `${path}.productEconomics`, 'productEconomics must be an object when present.'));
    } else {
      if (!['available', 'partial', 'unavailable'].includes(String(value.productEconomics.state))) {
        issues.push(issue('projection_shape_invalid', `${path}.productEconomics.state`, 'productEconomics.state must be available, partial, or unavailable.'));
      }
      // The attribution semantic is a pinned owner decision (2026-08-18):
      // money attributes to the product of its own cycle. A projection
      // claiming any other semantic must be rejected, not silently rendered.
      if (value.productEconomics.attribution !== 'per_cycle_product') {
        issues.push(issue('projection_shape_invalid', `${path}.productEconomics.attribution`, 'productEconomics.attribution must be per_cycle_product.'));
      }
      if (value.productEconomics.evidenceClass !== 'descriptive') {
        issues.push(issue('projection_shape_invalid', `${path}.productEconomics.evidenceClass`, 'productEconomics realized layer must carry the descriptive evidence class.'));
      }
      for (const key of ['products', 'reasonCodes', 'sourceRefs'] as const) {
        if (!Array.isArray(value.productEconomics[key])) {
          issues.push(issue('projection_shape_invalid', `${path}.productEconomics.${key}`, `productEconomics.${key} must be an array.`));
        }
      }
      const shareShapeIssues = (
        share: unknown,
        sharePath: string,
      ): void => {
        if (!isRecord(share)) {
          issues.push(issue('projection_shape_invalid', sharePath, 'share block must be an object.'));
          return;
        }
        if (!['available', 'below_floor', 'unavailable'].includes(String(share.state))) {
          issues.push(issue('projection_shape_invalid', `${sharePath}.state`, 'share state must be available, below_floor, or unavailable.'));
        }
        // C1-AC-012: the denominator is part of the claim — counts always travel.
        if (typeof share.numerator !== 'number' || typeof share.denominator !== 'number') {
          issues.push(issue('projection_shape_invalid', `${sharePath}.numerator`, 'share numerator/denominator counts are required.'));
        }
        // A percentage on a below-floor or unavailable denominator is a
        // precision claim the data cannot back — reject it, never render it.
        if (share.state !== 'available' && share.share !== undefined) {
          issues.push(issue('projection_shape_invalid', `${sharePath}.share`, 'share value is only allowed in the available state.'));
        }
      };
      if (Array.isArray(value.productEconomics.products)) {
        value.productEconomics.products.forEach((product, index) => {
          if (!isRecord(product)) return;
          const productPath = `${path}.productEconomics.products[${index}]`;
          if (product.projected !== undefined) {
            const projectedPath = `${productPath}.projected`;
            if (!isRecord(product.projected)) {
              issues.push(issue('projection_shape_invalid', projectedPath, 'projected must be an object when present.'));
            } else {
              if (!['sbg_monthly', 'sbg_weekly', 'upfront', 'one_time', 'unavailable']
                .includes(String(product.projected.modelClass))) {
                issues.push(issue('projection_shape_invalid', `${projectedPath}.modelClass`, 'projected.modelClass must be a known model class.'));
              }
              // The projected layer is predictive by definition — a descriptive or
              // causal-styled claim here is semantic drift, not a display choice.
              if (product.projected.evidenceClass !== 'predictive') {
                issues.push(issue('projection_shape_invalid', `${projectedPath}.evidenceClass`, 'projected.evidenceClass must be predictive.'));
              }
              if (product.projected.valueBasis !== 'gross_collected') {
                issues.push(issue('projection_shape_invalid', `${projectedPath}.valueBasis`, 'projected layer launches on the labeled gross audit basis.'));
              }
            }
          }
          if (product.tierMix !== undefined) {
            shareShapeIssues(product.tierMix, `${productPath}.tierMix`);
          }
          if (product.cancelIntent !== undefined) {
            const intentPath = `${productPath}.cancelIntent`;
            if (!isRecord(product.cancelIntent)) {
              issues.push(issue('projection_shape_invalid', intentPath, 'cancelIntent must be an object when present.'));
            } else {
              // D6 stage 1 pin: cancel intent is watch-only evidence. Any other
              // evidence class means a verdict/model input leaked in — reject.
              if (product.cancelIntent.evidenceClass !== 'watch_only') {
                issues.push(issue('projection_shape_invalid', `${intentPath}.evidenceClass`, 'cancelIntent must carry the watch_only evidence class.'));
              }
              shareShapeIssues(product.cancelIntent.pendingCancel, `${intentPath}.pendingCancel`);
            }
          }
        });
      }
      if (value.productEconomics.signals !== undefined) {
        const signalsPath = `${path}.productEconomics.signals`;
        if (!isRecord(value.productEconomics.signals)) {
          issues.push(issue('projection_shape_invalid', signalsPath, 'signals must be an object when present.'));
        } else {
          const signals = value.productEconomics.signals;
          if (signals.evidenceClass !== 'watch_only') {
            issues.push(issue('projection_shape_invalid', `${signalsPath}.evidenceClass`, 'signals must carry the watch_only evidence class.'));
          }
          if (!['available', 'partial', 'unavailable'].includes(String(signals.state))) {
            issues.push(issue('projection_shape_invalid', `${signalsPath}.state`, 'signals.state must be available, partial, or unavailable.'));
          }
          if (!isRecord(signals.planMix) || !Array.isArray((signals.planMix as Record<string, unknown>).entries)) {
            issues.push(issue('projection_shape_invalid', `${signalsPath}.planMix`, 'signals.planMix with an entries array is required.'));
          }
          // The rescind correction must always ride with the signals block —
          // a pending-cancel chip may never render without it (D6 stage 1).
          shareShapeIssues(signals.rescindHistory, `${signalsPath}.rescindHistory`);
        }
      }
      const payback = value.productEconomics.realizedPayback;
      if (!isRecord(payback)) {
        issues.push(issue('projection_shape_invalid', `${path}.productEconomics.realizedPayback`, 'productEconomics.realizedPayback is required.'));
      } else {
        if (!['covered', 'not_yet', 'unavailable'].includes(String(payback.state))) {
          issues.push(issue('projection_shape_invalid', `${path}.productEconomics.realizedPayback.state`, 'realizedPayback.state must be covered, not_yet, or unavailable.'));
        }
        if (payback.valueBasis !== 'gross_collected') {
          issues.push(issue('projection_shape_invalid', `${path}.productEconomics.realizedPayback.valueBasis`, 'realizedPayback must use the labeled gross audit basis.'));
        }
        // D9 pairing invariant: a payback date never travels without its
        // spend-coverage descriptor.
        if (!isRecord(payback.spendCoverage)) {
          issues.push(issue('projection_shape_invalid', `${path}.productEconomics.realizedPayback.spendCoverage`, 'realizedPayback.spendCoverage is required.'));
        }
      }
    }
  }
  if (value.fatigueCurve !== undefined) {
    if (!isRecord(value.fatigueCurve)) {
      issues.push(issue('projection_shape_invalid', `${path}.fatigueCurve`, 'fatigueCurve must be an object when present.'));
    } else {
      if (!['available', 'partial', 'unavailable'].includes(String(value.fatigueCurve.state))) {
        issues.push(issue('projection_shape_invalid', `${path}.fatigueCurve.state`, 'fatigueCurve.state must be available, partial, or unavailable.'));
      }
      for (const key of ['points', 'controls', 'reasonCodes'] as const) {
        if (!Array.isArray(value.fatigueCurve[key])) {
          issues.push(issue('projection_shape_invalid', `${path}.fatigueCurve.${key}`, `fatigueCurve.${key} must be an array.`));
        }
      }
    }
  }
  if (value.watchOnlyPltv !== undefined) {
    if (!isRecord(value.watchOnlyPltv)) {
      issues.push(issue('projection_shape_invalid', `${path}.watchOnlyPltv`, 'watchOnlyPltv must be an object when present.'));
    } else {
      if (!['available', 'not_decision_grade', 'unavailable'].includes(String(value.watchOnlyPltv.state))) {
        issues.push(issue('projection_shape_invalid', `${path}.watchOnlyPltv.state`, 'watchOnlyPltv.state must be available, not_decision_grade, or unavailable.'));
      }
      if (value.watchOnlyPltv.metricKey !== 'watch_only_pltv12_gross') {
        issues.push(issue('projection_shape_invalid', `${path}.watchOnlyPltv.metricKey`, 'watchOnlyPltv must use its canonical watch-only metric key.'));
      }
      if (value.watchOnlyPltv.valueBasis !== 'gross') {
        issues.push(issue('projection_shape_invalid', `${path}.watchOnlyPltv.valueBasis`, 'watchOnlyPltv.valueBasis must be the labeled gross audit basis.'));
      }
      for (const key of ['points', 'reasonCodes', 'sourceRefs'] as const) {
        if (!Array.isArray(value.watchOnlyPltv[key])) {
          issues.push(issue('projection_shape_invalid', `${path}.watchOnlyPltv.${key}`, `watchOnlyPltv.${key} must be an array.`));
        }
      }
      // Lane honesty (topup plan S2c): every point declares live|backtest and
      // the container carries SEPARATE per-lane day counts — an unknown lane
      // or missing counts must fail validation, never render unlabeled.
      for (const key of ['liveDays', 'backtestDays'] as const) {
        const count = value.watchOnlyPltv[key];
        if (typeof count !== 'number' || !Number.isFinite(count) || count < 0) {
          issues.push(issue('projection_shape_invalid', `${path}.watchOnlyPltv.${key}`, `watchOnlyPltv.${key} must be a non-negative number.`));
        }
      }
      if (Array.isArray(value.watchOnlyPltv.points)) {
        value.watchOnlyPltv.points.forEach((point, index) => {
          if (!isRecord(point) || (point.lane !== 'live' && point.lane !== 'backtest')) {
            issues.push(issue('projection_shape_invalid', `${path}.watchOnlyPltv.points[${index}].lane`, 'watchOnlyPltv point lane must be live or backtest.'));
          }
        });
      }
      // D2 split (C2-I2): both states must be present TOGETHER and valid —
      // a single derived string can never replace the two independent fields.
      if (value.watchOnlyPltv.cadenceSignal !== undefined) {
        const signal = value.watchOnlyPltv.cadenceSignal;
        if (!isRecord(signal)) {
          issues.push(issue('projection_shape_invalid', `${path}.watchOnlyPltv.cadenceSignal`, 'cadenceSignal must be an object when present.'));
        } else {
          if (!['insufficient', 'watch_only', 'decision_grade'].includes(String(signal.signalState))) {
            issues.push(issue('projection_shape_invalid', `${path}.watchOnlyPltv.cadenceSignal.signalState`, 'cadenceSignal.signalState must be insufficient, watch_only, or decision_grade.'));
          }
          if (!['not_mature', 'mature'].includes(String(signal.targetMaturityState))) {
            issues.push(issue('projection_shape_invalid', `${path}.watchOnlyPltv.cadenceSignal.targetMaturityState`, 'cadenceSignal.targetMaturityState must be not_mature or mature.'));
          }
          if (typeof signal.targetHorizonDays !== 'number' || !Number.isFinite(signal.targetHorizonDays)) {
            issues.push(issue('projection_shape_invalid', `${path}.watchOnlyPltv.cadenceSignal.targetHorizonDays`, 'cadenceSignal.targetHorizonDays must be a number.'));
          }
        }
      }
    }
  }
  if (value.filterContext !== undefined) {
    if (!isRecord(value.filterContext)) {
      issues.push(issue(
        'filter_context_shape_invalid',
        `${path}.filterContext`,
        'filterContext must be an object when present.',
      ));
    } else {
      issues.push(...unknownRootKeys(value.filterContext, FILTER_CONTEXT_KEYS, `${path}.filterContext`));
      for (const key of FILTER_CONTEXT_KEYS) {
        const dimension = value.filterContext[key];
        if (
          dimension !== undefined
          && (!Array.isArray(dimension) || dimension.some((entry) => !nonEmptyString(entry)))
        ) {
          issues.push(issue(
            'filter_context_shape_invalid',
            `${path}.filterContext.${key}`,
            `${key} must contain only non-empty string identifiers.`,
          ));
        }
      }
    }
  }
  if (value.copilotEvidenceRef !== undefined) {
    if (!isRecord(value.copilotEvidenceRef)) {
      issues.push(issue('copilot_evidence_ref_invalid', `${path}.copilotEvidenceRef`, 'Copilot evidence reference must be an object.'));
    } else {
      const unknown = Object.keys(value.copilotEvidenceRef)
        .filter((key) => !['snapshotId', 'projectionHash'].includes(key));
      if (unknown.length
        || !nonEmptyString(value.copilotEvidenceRef.snapshotId)
        || !/^[A-Za-z0-9][A-Za-z0-9._:@/+~=%-]{0,127}$/.test(String(value.copilotEvidenceRef.snapshotId))
        || !/^[a-f0-9]{64}$/.test(String(value.copilotEvidenceRef.projectionHash))) {
        issues.push(issue(
          'copilot_evidence_ref_invalid',
          `${path}.copilotEvidenceRef`,
          'Copilot evidence reference must contain only an opaque snapshot ID and SHA-256 projection hash.',
        ));
      }
    }
  }
  if (value.decisionHistory !== undefined && !Array.isArray(value.decisionHistory)) {
    issues.push(issue('projection_shape_invalid', `${path}.decisionHistory`, 'decisionHistory must be an array when present.'));
  }
  return dedupeIssues(issues);
}

function validateDecisionHistory(
  projection: CreativeDecisionProjection,
  issues: CreativeDecisionValidationIssue[],
): void {
  const history = projection.decisionHistory ?? [];
  const ids = new Set<string>();
  let previousAt = Number.NEGATIVE_INFINITY;
  for (const [index, revision] of history.entries()) {
    const path = `decisionHistory[${index}]`;
    if (!revision.decisionRevisionId || ids.has(revision.decisionRevisionId)) {
      issues.push(issue('decision_revision_id_invalid', `${path}.decisionRevisionId`, 'Decision revision IDs must be non-empty and unique.'));
    }
    ids.add(revision.decisionRevisionId);
    const effectiveAt = Date.parse(revision.effectiveAt);
    if (!Number.isFinite(effectiveAt) || effectiveAt < previousAt) {
      issues.push(issue('decision_history_order_invalid', `${path}.effectiveAt`, 'Decision revisions must be in append-only effective-time order.'));
    }
    previousAt = effectiveAt;
    if (index === 0 && revision.supersedesDecisionRevisionId) {
      issues.push(issue('decision_history_chain_invalid', `${path}.supersedesDecisionRevisionId`, 'The first included revision cannot supersede another included revision.'));
    }
    if (index > 0 && revision.supersedesDecisionRevisionId !== history[index - 1].decisionRevisionId) {
      issues.push(issue('decision_history_chain_invalid', `${path}.supersedesDecisionRevisionId`, 'Each later revision must supersede the immediately prior revision.'));
    }
  }
  const current = history[history.length - 1];
  if (current && (
    current.projectionId !== projection.projectionId
    || current.projectionVersion !== projection.projectionVersion
    || current.decision !== projection.recommendation.decision
    || current.dataState !== projection.dataState
  )) {
    issues.push(issue('decision_history_current_mismatch', 'decisionHistory', 'The latest decision revision must describe the current projection.'));
  }
}

function unknownRootKeys(
  value: Record<string, unknown>,
  allowed: Set<string>,
  path: string,
): CreativeDecisionValidationIssue[] {
  return Object.keys(value)
    .filter((key) => !allowed.has(key))
    .sort()
    .map((key) => issue('unknown_response_field', `${path}.${key}`, 'Unknown response fields are rejected by the client allowlist.'));
}

function privatePayloadIssues(value: unknown, path: string): CreativeDecisionValidationIssue[] {
  const issues: CreativeDecisionValidationIssue[] = [];
  const visit = (candidate: unknown, candidatePath: string): void => {
    if (Array.isArray(candidate)) {
      candidate.forEach((item, index) => visit(item, `${candidatePath}[${index}]`));
      return;
    }
    if (!isRecord(candidate)) {
      if (typeof candidate === 'string' && PRIVATE_VALUE.test(candidate)) {
        issues.push(issue('private_response_value', candidatePath, 'Private or credential-shaped response values are prohibited.'));
      }
      return;
    }
    for (const [key, nested] of Object.entries(candidate)) {
      const nestedPath = `${candidatePath}.${key}`;
      if (PRIVATE_KEY.test(key)) {
        issues.push(issue('private_response_field', nestedPath, 'Private or credential-shaped response fields are prohibited.'));
      }
      visit(nested, nestedPath);
    }
  };
  visit(value, path);
  return issues;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function nonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function dedupeIssues(issues: CreativeDecisionValidationIssue[]): CreativeDecisionValidationIssue[] {
  return [...new Map(issues.map((entry) => [`${entry.code}:${entry.path}`, entry])).values()];
}

export function validateAssignmentRevision(
  revision: AcquisitionAssignmentRevision,
): CreativeDecisionValidationIssue[] {
  const issues: CreativeDecisionValidationIssue[] = [];
  if (revision.weights.length === 0) {
    return [issue('assignment_empty', 'weights', 'An assignment revision needs at least one selected candidate.')];
  }
  const candidateIds = new Set<string>();
  let sum = 0;
  for (const [index, row] of revision.weights.entries()) {
    if (candidateIds.has(row.candidateId)) issues.push(issue('assignment_candidate_duplicate', `weights[${index}].candidateId`, 'A candidate can appear only once in one revision.'));
    candidateIds.add(row.candidateId);
    if (!Number.isFinite(row.weight) || row.weight <= 0 || row.weight > 1) {
      issues.push(issue('assignment_weight_invalid', `weights[${index}].weight`, 'Assignment weights must be finite and in (0, 1].'));
    }
    sum += row.weight;
  }
  if (revision.view === 'single_touch' && (revision.weights.length !== 1 || Math.abs(sum - 1) > 1e-9)) {
    issues.push(issue('single_touch_not_unique', 'weights', 'Single-touch assignment must select exactly one deployment at weight 1.'));
  }
  if (revision.view === 'fractional' && Math.abs(sum - 1) > 1e-9) {
    issues.push(issue('fractional_weights_do_not_reconcile', 'weights', 'Fractional assignment weights must sum to one.'));
  }
  return issues;
}

export function haveCompatibleMoneyAmounts(amounts: MoneyAmountBinding[]): boolean {
  if (amounts.length === 0) return true;
  if (amounts.some((amount) => (
    !amount.originalCurrency.trim()
    || !Number.isSafeInteger(amount.amountMinor)
    || !Number.isInteger(amount.currencyExponent)
    || amount.currencyExponent < 0
    || amount.currencyExponent > 9
  ))) return false;
  const nativeCurrencies = new Set(amounts.map((amount) => amount.originalCurrency.toUpperCase()));
  const nativeExponents = new Set(amounts.map((amount) => amount.currencyExponent));
  const policyVersions = new Set(amounts.map((amount) => amount.fxPolicyVersion).filter(Boolean));
  const hasVersionedPolicyEvidence = policyVersions.size > 0;
  const policyEvidenceCompatible = !hasVersionedPolicyEvidence
    || (policyVersions.size === 1 && amounts.every((amount) => Boolean(amount.fxPolicyVersion)));
  if (nativeCurrencies.size === 1) {
    return nativeExponents.size === 1 && policyEvidenceCompatible;
  }
  const reportingCurrencies = new Set(amounts.map((amount) => amount.reportingCurrency?.toUpperCase()).filter(Boolean));
  const reportingExponents = new Set(amounts.map((amount) => amount.reportingCurrencyExponent).filter((value) => value !== undefined));
  return reportingCurrencies.size === 1 && reportingExponents.size === 1 && amounts.every((amount) => (
    amount.reportingAmountMinor !== undefined
    && Number.isSafeInteger(amount.reportingAmountMinor)
    && amount.reportingCurrencyExponent !== undefined
    && Number.isInteger(amount.reportingCurrencyExponent)
    && amount.reportingCurrencyExponent >= 0
    && (
      (amount.fxRate !== undefined
        && Number.isFinite(amount.fxRate)
        && amount.fxRate > 0
        && Boolean(amount.fxRateSource)
        && Boolean(amount.fxRateAsOf))
      || (Boolean(amount.fxRateSources?.length)
        && Boolean(amount.fxRateAsOfDates?.length)
        && Boolean(amount.fxRateVersions?.length))
    )
    && (!hasVersionedPolicyEvidence || Boolean(amount.fxPolicyVersion))
  )) && policyEvidenceCompatible;
}

function issue(code: string, path: string, message: string): CreativeDecisionValidationIssue {
  return { code, path, message };
}
